import { useMemo } from "react";
import { useLingui } from "@lingui/react";
import { getSchemaOrder } from "src/utils/sizes/size-meta";
import { compareSizeNames } from "src/utils/sizes/sort-by";
import type { ListingFacetSizeData } from "src/types/adapters/listing.type";
import {
  OTHER_SIZE_SCHEMA,
  buildGroupKey,
  buildGroupTitle,
  normalizeSchema,
  normalizeUnit,
  prettifySizeId,
  unitRank,
} from "../utils/sizeSchema";

type SizeItem = {
  id: string;
  label: string;
  value: string;
  selected: boolean;
};

type SizesList = Record<string, SizeItem[]>;

export function useGroupedSizes(items: ListingFacetSizeData[]) {
  const { i18n } = useLingui();

  return useMemo(() => {
    if (!items.length) {
      return {
        schemas: [] as string[],
        mappedSizes: [] as [string, SizeItem[]][],
      };
    }

    const reducedSizes = items.reduce<SizesList>((acc, item) => {
      const schema = normalizeSchema(item.schema);
      const unit = normalizeUnit(item.unit);

      const groupKey = buildGroupKey({ schema, unit });
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }

      acc[groupKey].push({
        // Bare facet options have data.id === "" (helpers.ts); fall back to the
        // option value (always the unique size id) so chips don't collide on an
        // empty React key.
        id: item.id || item.value,
        // Prefer the synced name, then a real display_name, then derive a label
        // from the id itself — a bare option arrives with name="" and
        // display_name === value (the raw id), which we'd otherwise render as-is.
        label:
          item.name ||
          (item.label && item.label !== item.value
            ? item.label
            : prettifySizeId(item.value)),
        value: item.value,
        selected: item.selected,
      });

      return acc;
    }, {});

    // Added this filter because the data we're recieving still needs to be cleaned up, we're getting int sizes within the EU and other schemas
    const letterSizeRegex = /\b(?:\d*x+)?[sml](?:x+)?\b/;
    Object.keys(reducedSizes).forEach((key) => {
      const baseSchema = key.split("|")[0];
      if (
        baseSchema &&
        baseSchema !== OTHER_SIZE_SCHEMA &&
        baseSchema.toUpperCase() !== "INT"
      ) {
        reducedSizes[key] = reducedSizes[key].filter((item) => {
          const label = (item.label || "").toLowerCase();
          const isLetterSize = letterSizeRegex.test(label);
          return !isLetterSize;
        });
      }
      reducedSizes[key].sort((a, b) => compareSizeNames(a.label, b.label));
    });

    const schemaOrder = getSchemaOrder(i18n.locale);
    const groups = Object.keys(reducedSizes).map((key) => {
      const name = key.split("|")[0];
      const unit = key.split("|")[1] || undefined;
      const title = buildGroupTitle({ schema: name, unit });
      return {
        key,
        baseSchema: name,
        unit,
        title,
        items: reducedSizes[key],
      };
    });

    // "Other" is the fallback bucket and always sorts last, after unrecognised
    // schemas — which otherwise share its rank and can overtake it.
    const schemaRank = (schema: string) => {
      if (schema === OTHER_SIZE_SCHEMA) return Number.MAX_SAFE_INTEGER;
      const index = schemaOrder.indexOf(schema);
      return index === -1 ? Number.MAX_SAFE_INTEGER - 1 : index;
    };

    groups.sort((a, b) => {
      const aIndex = schemaRank(a.baseSchema);
      const bIndex = schemaRank(b.baseSchema);
      if (aIndex !== bIndex) return aIndex - bIndex;
      const ur = unitRank(a.unit) - unitRank(b.unit);
      if (ur !== 0) return ur;
      // fallback: alphabetical by title for stability
      return a.title.localeCompare(b.title);
    });

    const orderedTitles = groups.map((g) => g.title);
    const mapped = groups.map(
      (g) => [g.title, g.items] as [string, SizeItem[]]
    );

    return { schemas: orderedTitles, mappedSizes: mapped };
  }, [items, i18n.locale]);
}
